import { type Firestore, doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { COLLECTION_DES_SEQUENCES, type EtatDeSequence } from '@taki/data-model';

/**
 * L'ETAT DES NUMEROS AUTOMATIQUES D'UNE ORG : un document par champ `numero`,
 * `orgs/{orgId}/sequences/{cle}` (`cleDeSequence`, `docs/socle/champs-types.md`,
 * section 4), qui porte `{ prochain, dernier, updatedAt, updatedBy }`.
 *
 * CE N'EST PAS UNE FAMILLE, comme le branding ou l'environnement : un etat, pas
 * une declaration ni une fiche. Pas de `list`, pas de champs standard, pas de
 * vues, et il ne part pas a la publication d'une app.
 *
 * DEUX ECRIVAINS, ET ILS N'ECRIVENT PAS LA MEME CHOSE :
 * - LE SERVEUR (le declencheur des fiches, par l'Admin SDK) attribue un numero
 *   dans une transaction : il ecrit `dernier` et `prochain` ;
 * - LE CLIENT (Studio, Parametres de l'app) ne regle que `prochain`, et
 *   seulement au-dessus du dernier numero attribue. Les rules le refusent sinon
 *   (`firebase/firestore.rules`, bloc `sequences`) : c'est ce qui garantit
 *   l'unicite des numeros meme apres un reglage. Le refus d'ici dit la meme
 *   chose en francais, avant l'aller-retour.
 *
 * `dernier` N'EST JAMAIS ECRIT ICI, pas meme a `null` a la creation du
 * document : son absence vaut « aucun numero attribue », et les rules refusent
 * tout client qui le pose ou le change.
 */
export interface SequencesRepository {
  /** L'etat d'une sequence, ou `null` quand aucun numero n'a encore ete ni attribue ni regle. */
  lire(cle: string): Promise<EtatDeSequence | null>;
  /**
   * REGLER LE PROCHAIN NUMERO. Refuse un nombre qui n'est pas un entier de 1 ou
   * plus, et un numero inferieur ou egal au dernier attribue (relu avant).
   */
  reglerProchain(cle: string, prochain: number): Promise<void>;
}

/** La cle d'un document de sequence : un segment de chemin, non vide, sans `/`. */
function cleValide(cle: string): string {
  if (typeof cle !== 'string' || cle.trim() === '') throw new Error('La clé de la séquence est vide.');
  if (cle.includes('/')) throw new Error(`La clé de la séquence « ${cle} » contient un "/" : elle découperait le chemin du document.`);
  if (cle.length > 400) throw new Error('La clé de la séquence dépasse 400 caractères.');
  return cle;
}

/** Un entier positif lu d'un document, ou `null` pour tout le reste. */
const entierLu = (valeur: unknown): number | null =>
  typeof valeur === 'number' && Number.isInteger(valeur) && valeur >= 0 ? valeur : null;

/** L'etat d'un document lu : `prochain` illisible ou absent se deduit du dernier (dernier + 1, sinon 1). */
export function etatDeSequenceLu(data: Record<string, unknown> | undefined): EtatDeSequence | null {
  if (data === undefined) return null;
  const dernier = entierLu(data['dernier']);
  const prochain = entierLu(data['prochain']);
  const minimum = dernier === null ? 1 : dernier + 1;
  return { prochain: prochain === null || prochain < minimum ? minimum : prochain, dernier };
}

export function createSequencesRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): SequencesRepository {
  const reference = (cle: string) => doc(db, 'orgs', orgId, COLLECTION_DES_SEQUENCES, cleValide(cle));
  return {
    async lire(cle) {
      const snap = await getDoc(reference(cle));
      return snap.exists() ? etatDeSequenceLu(snap.data() as Record<string, unknown>) : null;
    },
    async reglerProchain(cle, prochain) {
      const ref = reference(cle);
      if (typeof prochain !== 'number' || !Number.isInteger(prochain) || prochain < 1) {
        throw new Error(`Le prochain numéro doit être un nombre entier de 1 ou plus (reçu ${String(prochain)}).`);
      }
      const snap = await getDoc(ref);
      const dernier = snap.exists() ? entierLu((snap.data() as Record<string, unknown>)['dernier']) : null;
      if (dernier !== null && prochain <= dernier) {
        throw new Error(`Le prochain numéro doit dépasser le dernier attribué (${dernier}) : ${prochain} donnerait un numéro déjà pris.`);
      }
      await setDoc(ref, { prochain, updatedAt: serverTimestamp(), updatedBy: getActorId() }, { merge: true });
    },
  };
}
